import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { motion, AnimatePresence } from "framer-motion";
import {
  getTodos,
  createTodo,
  deleteTodo as deleteTodoApi,
  toggleTodo as toggleTodoApi
} from "../services/customerApi";

export default function TodoPanel({ open, onClose }) {
  const [todos, setTodos] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  // Load todos when panel opens
  useEffect(() => {
    if (open) {
      loadTodos();
    }
  }, [open]);

  const loadTodos = async () => {
    try {
      setLoading(true);
      const data = await getTodos();
      setTodos(data || []);
    } catch (error) {
      console.error("Failed to load todos:", error);
      setTodos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTodo = async () => {
    if (!text.trim()) return;

    const todoText = text.trim();
    setText("");

    try {
      const createdTodo = await createTodo(todoText);
      setTodos(prev => [...prev, createdTodo]);
    } catch (error) {
      console.error("Failed to add todo:", error);
      // Fallback local state
      setTodos(prev => [...prev, {
        id: Date.now(),
        title: todoText,
        completed: false
      }]);
    }
  };

  const handleDeleteTodo = async (id) => {
    try {
      await deleteTodoApi(id);
      setTodos(prev => prev.filter(todo => todo.id !== id));
    } catch (error) {
      console.error("Failed to delete todo:", error);
      // Fallback local state
      setTodos(prev => prev.filter(todo => todo.id !== id));
    }
  };

  const handleToggleTodo = async (id, currentCompleted) => {
    try {
      await toggleTodoApi(id, !currentCompleted);
      setTodos(prev => prev.map(todo =>
        todo.id === id ? { ...todo, completed: !todo.completed } : todo
      ));
    } catch (error) {
      console.error("Failed to toggle todo:", error);
      // Fallback local state
      setTodos(prev => prev.map(todo =>
        todo.id === id ? { ...todo, completed: !todo.completed } : todo
      ));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAddTodo();
    }
  };

  const completedCount = todos.filter(todo => todo.completed).length;

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, x: 100 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.9, x: 100 }}
      transition={{
        type: "spring",
        damping: 20,
        stiffness: 300,
        mass: 0.5
      }}
      style={{
        transformOrigin: "right center",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          position: "fixed",
          right: 24,
          top: "20%",
          width: 280,
          background: "#111111",
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          zIndex: 1400,
          p: 1.5,
          border: "1px solid rgba(253, 184, 19, 0.15)",
          overflow: "hidden",
          "&:hover": {
            boxShadow: "0 25px 70px rgba(0,0,0,0.7)",
            transform: "translateX(-4px)",
            transition: "all 0.3s ease"
          },
          transition: "all 0.3s ease"
        }}
      >
        {/* Header */}
        <motion.div
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography
              sx={{
                fontSize: "1rem",
                background: "linear-gradient(45deg, #FDB813 30%, #ffffff 90%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 900,
                fontFamily: "'Outfit', sans-serif"
              }}
            >
              TASKS
            </Typography>
            <motion.div
              whileHover={{ rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  background: "linear-gradient(45deg, #FF416C 30%, #FF4B2B 90%)",
                  color: "white",
                  width: 28,
                  height: 28
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </motion.div>
          </Box>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Divider
              sx={{
                my: 1,
                background: "linear-gradient(to right, transparent, rgba(253, 184, 19, 0.5), transparent)",
                height: 1
              }}
            />
          </motion.div>
        </motion.div>

        {/* Add Todo */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Add task..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyPress={handleKeyPress}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.85rem",
                  color: "#ffffff",
                  fontFamily: "'Outfit', sans-serif",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
                  "&:hover fieldset": { borderColor: "#FDB813" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#FDB813",
                    boxShadow: "0 0 0 2px rgba(253, 184, 19, 0.1)"
                  }
                }
              }}
            />
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Button
                variant="contained"
                onClick={handleAddTodo}
                sx={{
                  background: "#FDB813",
                  color: "#111111",
                  minWidth: "auto",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.75rem",
                  fontWeight: 800,
                  "&:hover": { background: "#e5a100" }
                }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </motion.div>
          </Box>
        </motion.div>

        {/* Todo List */}
        <Box sx={{
          maxHeight: 220,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(253, 184, 19, 0.3)",
            borderRadius: "2px",
            "&:hover": {
              background: "rgba(253, 184, 19, 0.5)",
            }
          }
        }}>
          <AnimatePresence>
            {loading ? (
              <Typography sx={{ textAlign: "center", p: 2, fontSize: "0.85rem", color: "text.secondary" }}>
                Loading...
              </Typography>
            ) : (
              todos.map((todo, index) => (
                <motion.div
                  key={todo.id || index}
                  initial={{ opacity: 0, x: -20, height: 0 }}
                  animate={{ opacity: 1, x: 0, height: "auto" }}
                  exit={{ opacity: 0, x: 20, height: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                    delay: index * 0.03
                  }}
                  layout
                >
                  <ListItem
                    sx={{
                      mb: 0.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      background: index % 2 === 0 ? "rgba(255, 255, 255, 0.03)" : "transparent",
                      minHeight: 36,
                      "&:hover": {
                        background: "rgba(33, 150, 243, 0.08)",
                        transform: "translateX(2px)",
                      },
                      transition: "all 0.2s ease"
                    }}
                    secondaryAction={
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteTodo(todo.id)}
                          size="small"
                          sx={{ width: 24, height: 24 }}
                        >
                          <DeleteIcon sx={{ fontSize: "0.9rem", color: "#f44336" }} />
                        </IconButton>
                      </motion.div>
                    }
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Checkbox
                        size="small"
                        checked={todo.completed || false}
                        onChange={() => handleToggleTodo(todo.id, todo.completed)}
                        sx={{
                          color: "#FDB813",
                          padding: 0.5,
                          "&.Mui-checked": { color: "#4CAF50" }
                        }}
                      />
                    </motion.div>
                    <ListItemText
                      primary={todo.title || todo}
                      sx={{
                        ml: 0.5,
                        "& .MuiListItemText-primary": {
                          fontSize: "0.85rem",
                          fontWeight: todo.completed ? 400 : 500,
                          color: todo.completed ? "#4CAF50" : "text.primary",
                          textDecoration: todo.completed ? "line-through" : "none",
                          opacity: todo.completed ? 0.7 : 1
                        }
                      }}
                    />
                  </ListItem>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </Box>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Box sx={{
            mt: 1.5,
            pt: 1,
            borderTop: "1px solid rgba(0,0,0,0.06)",
            textAlign: "center"
          }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.7rem",
                fontWeight: 500
              }}
            >
              {todos.length} tasks • {completedCount} completed
            </Typography>
          </Box>
        </motion.div>
      </Box>
    </motion.div>
  );
}